import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { FaTrash, FaBan } from "react-icons/fa";

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [reportsPerPage] = useState(10);

  const navigate = useNavigate();
  const { user, fetchWithRetry, isLoading, logout } = useAuth();

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchWithRetry({
        method: "get",
        url: "/api/admin/reports",
      });
      setReports(res.data);
      setFilteredReports(res.data);
      setLoading(false);
    } catch (error) {
      console.error("Lỗi tải danh sách báo cáo:", error);
      setError(
        error.response?.data?.message ||
        (error.response?.status === 403
          ? "Bạn không có quyền truy cập trang này! Vui lòng đăng nhập lại."
          : "Không thể tải danh sách báo cáo!")
      );
      if (error.response?.status === 403 || error.response?.status === 401) {
        setTimeout(() => logout(), 3000);
      }
      setLoading(false);
    }
  }, [fetchWithRetry, logout]);

  const handleDeleteReport = useCallback(
    async (reportId) => {
      if (!window.confirm("Bạn có chắc muốn xóa báo cáo này?")) return;
      try {
        await fetchWithRetry({
          method: "post",
          url: "/api/admin/reports/delete",
          data: { reportId },
        });
        setReports((prevReports) => prevReports.filter((report) => report._id !== reportId));
        setFilteredReports((prevReports) => prevReports.filter((report) => report._id !== reportId));
        setSuccessMessage("Xóa báo cáo thành công!");
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error) {
        console.error("Lỗi xóa báo cáo:", error);
        setError(error.response?.data?.message || "Không thể xóa báo cáo!");
      }
    },
    [fetchWithRetry]
  );

  const handleBanUser = useCallback(
    async (userId) => {
      if (!window.confirm("Bạn có chắc muốn khóa người dùng này?")) return;
      try {
        await fetchWithRetry({
          method: "post",
          url: "/api/admin/users/ban",
          data: { userId },
        });
        setSuccessMessage("Khóa người dùng thành công!");
        setTimeout(() => setSuccessMessage(null), 3000);
        await loadReports();
      } catch (error) {
        console.error("Lỗi khóa người dùng:", error);
        setError(error.response?.data?.message || "Không thể khóa người dùng!");
      }
    },
    [fetchWithRetry, loadReports]
  );

  useEffect(() => {
    let filtered = [...reports];

    if (searchTerm) {
      filtered = filtered.filter(
        (report) =>
          (report.reportedUser?.name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
          (report.reportedUser?.email?.toLowerCase() || "").includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus) {
      filtered = filtered.filter((report) => report.status === filterStatus);
    }

    setFilteredReports(filtered);
    setCurrentPage(1);
  }, [reports, searchTerm, filterStatus]);

  const indexOfLastReport = currentPage * reportsPerPage;
  const indexOfFirstReport = indexOfLastReport - reportsPerPage;
  const currentReports = filteredReports.slice(indexOfFirstReport, indexOfLastReport);
  const totalPages = Math.ceil(filteredReports.length / reportsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    if (isLoading) return;

    if (!user || !user.permissions.includes("view_reports")) {
      setError("Bạn không có quyền truy cập trang này!");
      navigate("/login");
      return;
    }
    loadReports();
  }, [user, navigate, loadReports, isLoading]);

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800">Quản lý báo cáo</h2>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}
      {successMessage && (
        <p className={`mb-4 ${successMessage.includes("thành công") ? "text-green-500" : "text-red-500"}`}>
          {successMessage}
        </p>
      )}

      <div className="flex flex-wrap gap-4 mb-6 items-center">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Tìm kiếm theo tên hoặc email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="min-w-[150px]">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tất cả trạng thái</option>
            <option value="pending">Chưa xử lý</option>
            <option value="resolved">Đã xử lý</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-center text-gray-500">Đang tải...</p>
      ) : currentReports.length === 0 ? (
        <p className="text-center text-gray-500">Không có báo cáo nào để hiển thị.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full bg-white rounded-lg shadow-lg">
            <thead>
              <tr className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                <th className="p-4 text-left font-semibold">Người báo cáo</th>
                <th className="p-4 text-left font-semibold">Người bị báo cáo</th>
                <th className="p-4 text-left font-semibold">Nội dung</th>
                <th className="p-4 text-left font-semibold">Trạng thái</th>
                <th className="p-4 text-left font-semibold">Hành động</th>
              </tr>
            </thead>
            <tbody>
              {currentReports.map((report) => (
                <tr key={report._id} className="border-b hover:bg-gray-50 transition duration-200">
                  <td className="p-4 text-gray-800">{report.reporter?.name || "N/A"}</td>
                  <td className="p-4 text-gray-800">{report.reportedUser?.name || "N/A"}</td>
                  <td className="p-4 text-gray-600">{report.content || "Không có nội dung"}</td>
                  <td className="p-4 text-gray-600">{report.status || "Chưa xử lý"}</td>
                  <td className="p-4 space-x-2">
                    <button
                      onClick={() => handleBanUser(report.reportedUser?._id)}
                      className="px-3 py-1 rounded bg-yellow-500 text-white hover:bg-yellow-600 transition duration-200"
                      title="Khóa người dùng"
                    >
                      <FaBan />
                    </button>
                    <button
                      onClick={() => handleDeleteReport(report._id)}
                      className="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 transition duration-200"
                      title="Xóa báo cáo"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <nav className="inline-flex rounded-md shadow">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            >
              Trước
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => paginate(page)}
                className={`px-3 py-1 border border-gray-300 ${
                  currentPage === page
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-500 hover:bg-gray-100"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded-r-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            >
              Sau
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default Reports;